// src/components/enterprise/ExecutiveOverview.jsx
//
// The Overview is the roll-up: it summarises each detail tab using the SAME
// canonical figures (so nothing contradicts the tab it links to) and routes
// into them via onNavigate. All discipline UI comes from the shared module.

import React from 'react';
import {
    Activity, TrendingUp, ArrowRight, Gauge, LineChart as LineIcon,
    DollarSign, MessageSquare, Users, Building2, Scale
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { ConfidenceTag, SourceTag, DisciplineFooter } from './shared/measurement';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';
import { deferredConsoleTabs, DEFERRED_TAB_NOTE } from './consoleTabs';
import { makeFormatters, trendDelta } from '../../utils/executiveMetrics';
import { earlyWarningRows, headlineFor, hasDirection } from '../../utils/earlyWarning';
import { claimsOutlookSummary, hasClaimsRecord } from '../../utils/claimsOutlook';
import RunningHotPanel from './RunningHotPanel';
import EarlyWarningPanel from './EarlyWarningPanel';

const ExecutiveOverview = ({ onNavigate, live, metrics }) => {
    const go = (tab) => () => { if (onNavigate) onNavigate(tab); };

    // Copy/labels come from the edition; NUMBERS come from the live snapshot
    // when one loaded (T1.3), falling back to the edition's modeled example.
    // Claims-outlook and financial cards stay modeled until Phase C's
    // leading→lagging join — their own 'Modeled' tags remain accurate.
    const edition = useEdition();
    const { orgData } = useOrganization();
    const deferred = deferredConsoleTabs(orgData);
    const isLive = live?.source === 'live';

    const CAPACITY_TREND = (isLive && live.capacityTrend?.length > 0)
        ? live.capacityTrend
        : edition.capacityTrend;
    const liveTrendDelta = trendDelta(CAPACITY_TREND);
    const fmt = makeFormatters(edition.roi?.locale, edition.roi?.currency);
    const capacityNoun = (isLive && live.capacityBasis === 'eri') ? 'ERI (performance + wellbeing + cognitive)' : 'capacity';
    const topLiveGroup = isLive ? (live.groups || []).find(g => !g.suppressed && g.pressure !== null) : null;

    const S = {
        ...edition.summary,
        ...(isLive ? {
            capacityScore: live.capacityScore ?? edition.summary.capacityScore,
            capacityTrend: liveTrendDelta,
            participationRate: live.participation?.rate ?? edition.summary.participationRate,
            topPriorityGroup: topLiveGroup ? topLiveGroup.name : edition.summary.topPriorityGroup,
            // Outlook + financial impact come from the executive metric layer
            // (ERI-adjusted, built on this org's inputs) once it has what it needs.
            ...(metrics?.outlook?.basis === 'live' ? {
                modeledStdClaims: { low: metrics.outlook.low, high: metrics.outlook.high, windowDays: metrics.outlook.windowDays },
                highSignalCohort: metrics.cohort?.strainCount ?? edition.summary.highSignalCohort
            } : {}),
            ...(metrics?.financial?.complete ? { modeledClaimsAvoidance: fmt.compact(metrics.financial.grossValue) } : {})
        } : {})
    };
    const financialSub = (isLive && !metrics?.financial?.complete)
        ? 'Add eligible employees + loaded labour cost in the ROI tab to model value'
        : `Modeled ${S.financialNoun || 'value'} · model it in full with your inputs`;
    const signalLabel = (isLive && live.signalBasis === 'running_hot') ? 'Running Hot share (%)' : (edition.trendSignalLabel || 'Leave signal');
    // The Needs Support line, once the snapshot function carries it.
    const hasNeedsSupportTrend = isLive && (live.capacityTrend || []).some(p => typeof p.needsSupportPct === 'number');
    // Live deltas can be negative; the modeled example never was.
    const trendLabel = S.capacityTrend >= 0 ? `+${S.capacityTrend}` : `${S.capacityTrend}`;
    // Which way, how fast, why, and what next (utils/earlyWarning.js): the hero
    // reads the fastest-rising group once the snapshot carries direction.
    const headline = hasDirection(live) ? headlineFor(earlyWarningRows(live, edition)) : null;

    // The organization’s own claims record, once on file (utils/claimsOutlook.js):
    // the outlook card then reads its figures and the sectors flagged early.
    const claims = hasClaimsRecord(live) ? claimsOutlookSummary(live, edition) : null;
    const recordOwner = (edition.predictive && edition.predictive.recordOwner) || 'the organization';

    const cards = [
        {
            tab: 'cc-readiness', icon: Gauge, title: 'Readiness & Strain', view: 'readiness detail',
            value: `${S.capacityScore}`, unit: '/100', trend: trendLabel,
            sub: `Priority group · ${S.topPriorityGroup}`,
            tags: <><SourceTag modeled={false} /><ConfidenceTag level="Leading" /></>
        },
        claims ? {
            tab: 'cc-predictive', icon: LineIcon, title: S.outlookTitle || 'Outlook', view: 'where the next claims are forming',
            value: claims.org.registered === null ? '—' : claims.org.registered.toLocaleString(), unit: `${S.outlookUnit || 'claims'} / yr`,
            sub: `At ${recordOwner}’s own rate · ${claims.early.length ? `${claims.early.length} sector${claims.early.length === 1 ? '' : 's'} reading early warning: ${claims.early.map((r) => r.name).join(', ')}` : 'no sector reading early warning this month'}`,
            tags: <ConfidenceTag level="Lagging" />
        } : {
            tab: 'cc-predictive', icon: LineIcon, title: S.outlookTitle || 'Outlook', view: 'predictive models',
            value: `${S.modeledStdClaims.low}\u2013${S.modeledStdClaims.high}`, unit: S.outlookUnit || '',
            sub: `Modeled ${S.outlookNoun || 'outcomes'} over ${S.modeledStdClaims.windowDays} days · ${S.highSignalCohort}-person high-signal cohort`,
            tags: <><SourceTag modeled /><ConfidenceTag level="Lagging" /></>
        },
        {
            tab: 'cc-roi', icon: DollarSign, title: 'Financial Impact', view: 'the full model',
            value: (isLive && !metrics?.financial?.complete) ? '—' : S.modeledClaimsAvoidance, unit: (isLive && !metrics?.financial?.complete) ? '' : 'midpoint',
            sub: financialSub,
            tags: <><SourceTag modeled /></>
        },
        {
            tab: 'cc-feedback', icon: MessageSquare, title: 'Anonymous Pulse', view: 'pulse & sentiment',
            value: `${S.participationRate}%`, unit: 'participation',
            sub: `Top theme · ${S.topPulseTheme}`,
            tags: <><SourceTag modeled={false} /><ConfidenceTag level="Leading" /></>
        }
    ].filter((c) => !(edition.hiddenTabs || []).includes(c.tab))
        // A tab on hold shows no modeled figure: the card says what it is waiting for.
        .map((c) => (deferred.includes(c.tab) ? {
            ...c, value: '—', unit: '', trend: null, view: 'what it will use',
            sub: 'Switches on after 90 days of this organization’s own check-ins and figures. Nothing modeled is shown before then.',
            tags: <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500">{DEFERRED_TAB_NOTE}</span>
        } : c));

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 no-print">

            {/* HERO — synthesis, consistent with the readiness tab */}
            <div className="bg-[var(--brand-deep)] rounded-xl overflow-hidden shadow-md no-print">
                <div className="p-6 md:p-8 flex flex-col lg:flex-row gap-8 items-center">
                    <div className="flex-1 space-y-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <Activity className="text-[var(--brand-yellow)]" size={24} strokeWidth={2} />
                            <h2 className="text-xl font-semibold text-white tracking-wide">{(edition.pageTitles && edition.pageTitles['cc-overview']) || 'Readiness Overview'}</h2>
                            <SourceTag dark modeled={false} />
                        </div>
                        <div className="flex items-baseline gap-3">
                            <span className="text-5xl md:text-6xl font-semibold text-white tracking-tight">{S.capacityScore}</span>
                            <span className="text-sm font-medium text-slate-400 uppercase tracking-wider">/100 capacity</span>
                            <span className={`flex items-center gap-1 text-sm font-semibold ${S.capacityTrend >= 0 ? 'text-emerald-300' : 'text-amber-300'}`}>
                                <TrendingUp size={14} /> {trendLabel}
                            </span>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed font-light max-w-xl">
                            {headline
                                ? <>Aggregate {capacityNoun} is at {S.capacityScore}/100, {S.capacityTrend >= 0 ? 'up' : 'down'} {Math.abs(S.capacityTrend)} over the period. {headline.text} An observed pattern, not a forecast.</>
                                : <>Aggregate {capacityNoun} is holding at {S.capacityScore}/100, {S.capacityTrend >= 0 ? 'up' : 'down'} {Math.abs(S.capacityTrend)} over the period. The {S.topPriorityGroup} group
                                    carries the most strain and is the clearest place to prioritise support earlier — an observed pattern, not a forecast.</>}
                        </p>
                    </div>
                    <div className="w-full lg:w-[320px] bg-white/5 p-5 rounded-lg border border-white/10 shrink-0">
                        <p className="text-[var(--brand-yellow)] font-semibold text-[11px] uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Activity size={14} /> Suggested next step
                        </p>
                        <p className="text-slate-200 text-sm leading-relaxed font-light mb-4">
                            {headline ? <><span className="font-semibold text-white">{headline.group}.</span> {headline.nextStep}</> : <>Review the {S.topPriorityGroup} group in the readiness detail and share the relevant support playbook.</>}
                        </p>
                        <button
                            onClick={go('cc-readiness')}
                            className="w-full flex items-center justify-center gap-2 bg-[var(--brand-blue)] hover:opacity-90 text-white font-semibold py-2 rounded-md text-sm transition-colors shadow-sm"
                        >
                            Open readiness detail <ArrowRight size={15} />
                        </button>
                    </div>
                </div>
            </div>

            {/* THREE LEGS — what every figure on this page stands on, and the way to the triangulation */}
            {(() => {
                const inputs = metrics?.inputs || {};
                // Figures on file: the headline inputs typed or uploaded, plus every dictionary id in the
                // warehouse for this organization (org-wide or by group), so a sector whose pack has no
                // headline input (a school division, a public program) is not shown as having none.
                const typedOrUploaded = Object.values(inputs).filter(v => v && (v.source === 'client' || v.source === 'upload')).length;
                const onFileIds = new Set([...Object.keys(live?.business || {}), ...Object.values(live?.businessByGroup || {}).flatMap(g => Object.keys(g || {}))]);
                const own = Math.max(typedOrUploaded, onFileIds.size);
                const byGroup = live?.businessByGroup ? Object.keys(live.businessByGroup).length : 0;
                const legs = [
                    { Icon: Users, k: 'Members report', v: isLive ? `${live.participation?.responded ?? '—'} members, live` : 'Modeled example', d: isLive ? `groups of 5+, last ${live.windowDays || 90} days` : 'until five members check in', on: isLive },
                    { Icon: Building2, k: "The organization's figures", v: own ? `${own} on file` : 'None yet', d: byGroup ? `${byGroup} group${byGroup === 1 ? '' : 's'} broken out` : 'typed or uploaded by group', on: own > 0, to: 'cc-data' },
                    { Icon: Scale, k: 'The business average', v: 'Sector reference', d: 'the comparison, always on', on: true }
                ];
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 no-print">
                        {legs.map(({ Icon, k, v, d, on, to }) => (
                            <button key={k} type="button" onClick={go(to || 'cc-triangulation')} className="text-left bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3 hover:border-[var(--brand-blue)] transition-colors">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}><Icon size={17} /></div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{k}</p>
                                    <p className="font-bold text-[var(--brand-deep)] leading-tight">{v}</p>
                                    <p className="text-[11px] text-slate-500 mt-0.5">{d}</p>
                                </div>
                                <ArrowRight size={14} className="ml-auto text-slate-300 shrink-0 mt-1" />
                            </button>
                        ))}
                    </div>
                );
            })()}

            {/* RUNNING HOT — the cohort only this product scores (T2.3) */}
            <RunningHotPanel live={live} metrics={metrics} onNavigate={onNavigate} />

            {/* WHERE IT IS HEADING — direction, movers, what leads, next step (renders once the snapshot carries direction) */}
            <EarlyWarningPanel live={live} onNavigate={onNavigate} />

            {/* ROLL-UP CARDS — each summarises and links to its tab */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {cards.map((c) => {
                    const Icon = c.icon;
                    return (
                        <button
                            key={c.tab}
                            onClick={go(c.tab)}
                            className="text-left bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:border-[var(--brand-blue)] hover:shadow-md transition-all flex flex-col justify-between group"
                        >
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-[var(--brand-deep)] flex items-center justify-center">
                                        <Icon size={20} strokeWidth={2} />
                                    </div>
                                    <div className="flex items-center gap-1.5">{c.tags}</div>
                                </div>
                                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{c.title}</h3>
                                <div className="flex items-baseline gap-1.5 mb-1">
                                    <span className="text-3xl font-bold text-[var(--brand-deep)] tracking-tight">{c.value}</span>
                                    <span className="text-xs font-medium text-slate-400">{c.unit}</span>
                                    {c.trend && <span className="text-xs font-semibold text-emerald-600 ml-1">{c.trend}</span>}
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed font-light">{c.sub}</p>
                            </div>
                            <span className="flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-blue)] mt-5 pt-4 border-t border-slate-100 group-hover:gap-2 transition-all">
                                View {c.view} <ArrowRight size={13} />
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* TREND CHART — ends at the same 82 as the headline */}
            <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-[var(--brand-deep)]">{(isLive && live.signalBasis === 'running_hot') ? (hasNeedsSupportTrend ? 'ERI vs. the Two Cohorts' : 'ERI vs. Running Hot Trend') : 'Capacity vs. Leave-Signal Trend'}</h3>
                            <SourceTag modeled={false} />
                        </div>
                        <p className="text-xs text-slate-500 font-light mt-1">An observed association over the period — not a forecast.</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-medium text-slate-600">
                        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[var(--brand-blue)]"></div> {isLive && live.capacityBasis === 'eri' ? 'ERI' : 'Capacity score'}</div>
                        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-500"></div> {signalLabel}</div>
                        {hasNeedsSupportTrend && <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-rose-600"></div> Needs Support share (%)</div>}
                    </div>
                </div>

                <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={CAPACITY_TREND} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="ovCapacity" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={edition.palette.blue} stopOpacity={0.2} />
                                    <stop offset="95%" stopColor={edition.palette.blue} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="ovSignal" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="ovNeeds" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#e11d48" stopOpacity={0.18} />
                                    <stop offset="95%" stopColor="#e11d48" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }} />
                            <Area type="monotone" dataKey="capacity" name={isLive && live.capacityBasis === 'eri' ? 'ERI' : 'Capacity score'} stroke={edition.palette.blue} strokeWidth={2} fillOpacity={1} fill="url(#ovCapacity)" connectNulls />
                            <Area type="monotone" dataKey="signal" name={signalLabel} stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#ovSignal)" connectNulls />
                            {hasNeedsSupportTrend && <Area type="monotone" dataKey="needsSupportPct" name="Needs Support share (%)" stroke="#e11d48" strokeWidth={2} fillOpacity={1} fill="url(#ovNeeds)" connectNulls />}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <DisciplineFooter caption="The Overview reflects the same figures as each detail tab. Observed signals are aggregate and leading; claims and financial impact are modeled, conservatively attributed, and confirmed only against an employer baseline. EAS reports what it measures and labels what it models." />
        </div>
    );
};

export default ExecutiveOverview;
